'use client';

import React, { createContext, useCallback, useContext, useMemo, useRef } from 'react';

export type FilePickerRequest = {
    accept?: string;
    multiple?: boolean;
    onSelect: (files: File[]) => void;
};

export type FilePickerController = {
    open: (request: FilePickerRequest) => void;
};

export type FilePickerProviderProps = {
    children: React.ReactNode;
};

const FilePickerContext = createContext<FilePickerController | null>(null);

/** Provides a persistent native file input to descendant picker triggers. */
const FilePickerProvider: React.FC<FilePickerProviderProps> = ({ children }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const requestRef = useRef<FilePickerRequest | null>(null);

    /** Opens the native file dialog synchronously within the caller's user interaction. */
    const open = useCallback((request: FilePickerRequest) => {
        const input = inputRef.current;
        if (!input) return;

        requestRef.current = request;
        input.accept = request.accept || '';
        input.multiple = request.multiple || false;
        input.value = '';
        input.click();
    }, []);

    /** Returns the selected files to the request that opened the dialog. */
    const onChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const input = event.currentTarget;
        const request = requestRef.current;
        const files = Array.from(input.files || []);

        requestRef.current = null;
        input.value = '';

        if (!request || !files.length) return;
        request.onSelect(request.multiple ? files : files.slice(0, 1));
    }, []);

    const controller = useMemo(() => ({ open }), [open]);

    return (
        <FilePickerContext.Provider value={controller}>
            {children}
            <input ref={inputRef} type="file" hidden onChange={onChange} />
        </FilePickerContext.Provider>
    );
};

/** Returns the nearest file-picker controller. */
const useFilePickerController = (): FilePickerController => {
    const controller = useContext(FilePickerContext);

    if (!controller) throw new Error('FilePicker.useController must be used within FilePicker.Provider');

    return controller;
};

export const FilePicker = {
    Provider: FilePickerProvider,
    useController: useFilePickerController,
};
